import { useState, useEffect } from 'react';
import { analysisApi, missionsApi } from '../../services/api';
import { useCanonical } from '../../hooks/useCanonical';
import { useAuthStore } from '../../store/authStore';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Spinner from '../../components/common/Spinner';
import Alert from '../../components/common/Alert';
import { useNavigate } from 'react-router-dom';
import {
  Brain,
  Search,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  Calendar,
  Activity,
  ChevronRight,
  Flame,
  Thermometer,
  ShieldCheck,
  X,
} from 'lucide-react';

interface AnalysisRecord {
  id: string;
  mission_id: string;
  analysis_type: string;
  confidence?: number;
  created_at: string;
  metadata?: {
    issue_classification?: string;
    severity_level?: string;
    affected_area_ha?: number;
    narrative?: string;
    [key: string]: any;
  };
}

interface Mission {
  id: string;
  name: string;
  status: string;
  mission_type: string;
  updated_at: string;
  metadata?: { [key: string]: any };
}

export default function AnalysisPage() {
  useCanonical('/analysis');
  const navigate = useNavigate();
  const { organizationModules } = useAuthStore();
  const hasOilGas  = organizationModules.includes('mvp3_oil_gas');
  const hasAgroTech = organizationModules.includes('mvp1_agrotech');
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResult, setSearchResult] = useState<{ answer: string; sources: any[]; fallback?: boolean } | null>(null);
  const [searchError, setSearchError] = useState('');

  const [analyses, setAnalyses] = useState<AnalysisRecord[]>([]);
  const [missions, setMissions] = useState<Mission[]>([]);
  const [loadingData, setLoadingData] = useState(true);

  // Load recent analyses + missions on mount
  useEffect(() => {
    (async () => {
      try {
        setLoadingData(true);
        const [aRes, mRes] = await Promise.all([
          analysisApi.getAll({ limit: 20 }),
          missionsApi.getAll({ limit: 100 }),
        ]);
        // getAll returns { success, data: { analysis: [...] } }
        const aList = aRes.data?.analysis ?? aRes.data ?? [];
        setAnalyses(Array.isArray(aList) ? aList : []);

        const mList = mRes.data?.missions ?? mRes.data ?? [];
        // Show drone_survey (AgroTech) and ogi_survey (Oil & Gas) missions with AI analysis complete
        const aiMissions = (Array.isArray(mList) ? mList : []).filter(
          (m: Mission) =>
            (m.mission_type === 'drone_survey' || m.mission_type === 'ogi_survey') &&
            m.metadata?.ai_analysis_status === 'complete'
        );
        setMissions(aiMissions);
      } catch {
        // Swallow — page still works, just shows empty state
      } finally {
        setLoadingData(false);
      }
    })();
  }, []);

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    setSearchError('');
    setSearchResult(null);

    try {
      const response = await analysisApi.processNLQ({ query: searchQuery });
      // Backend now returns: { success: true, data: { answer, sources } }
      const data = response.data ?? response;
      setSearchResult({
        answer:   data.answer  ?? data.result ?? 'No answer returned.',
        sources:  data.sources ?? [],
        fallback: data.fallback ?? false,
      });
    } catch (err: any) {
      setSearchError(
        err.response?.data?.message || 'Query failed. Please try again.'
      );
    } finally {
      setSearching(false);
    }
  };

  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

  const totalConfidence =
    analyses.length > 0
      ? analyses.reduce((s, a) => s + (a.confidence ?? 0), 0) / analyses.length
      : 0;

  const AGROTECH_QUERIES = [
    'What disease was found in the East Block Tea Garden?',
    'What is the NDVI reading for the affected zone?',
    'What spray treatments are recommended?',
    'How large is the affected area in hectares?',
  ];

  const OIL_GAS_QUERIES = [
    'Show me vegetation die-off along Pipeline Sector 7 this month',
    'Are there any thermal anomalies above baseline on the Escravos corridor?',
    'What is the estimated plume emission rate for the last OGI survey?',
    'Which pipeline corridors have unresolved anomalies pending review?',
  ];

  const EXAMPLE_QUERIES = hasOilGas
    ? [...OIL_GAS_QUERIES, ...(hasAgroTech ? AGROTECH_QUERIES : [])]
    : AGROTECH_QUERIES;

  return (
    <div className="space-y-6">

      {/* ── Header ── */}
      <div>
        <h1 className="text-3xl font-display font-bold text-gray-900 dark:text-white">
          AI Analysis
        </h1>
        <p className="text-gray-600 dark:text-gray-300 mt-1">
          Ask questions in plain English and get instant answers about your field conditions, diagnoses, and recommended actions
        </p>
      </div>

      {/* ── NLQ Search ── */}
      <Card>
        <div className="flex items-center gap-2 mb-4">
          <Brain className="w-6 h-6 text-primary-600 dark:text-vivid-lime-bright" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Ask Anything
          </h2>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
          Ask questions in plain English and get instant answers about your field conditions, diagnoses, and recommended actions.
        </p>

        <div className="flex gap-2">
          <div className="relative flex-1">
            <input
              className="w-full px-4 py-2 pr-9 border-2 rounded-lg focus:outline-none focus:ring-0 transition-colors bg-white dark:bg-metallic-800 text-gray-900 dark:text-white border-gray-300 dark:border-vivid-purple-dark/50 focus:border-primary-500 dark:focus:border-vivid-cyan-bright"
              placeholder="e.g., What disease was detected in the East Block Tea Garden?"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                aria-label="Clear input"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <Button onClick={handleSearch} loading={searching} disabled={!searchQuery.trim()}>
            <Search className="w-4 h-4 mr-2" />
            Ask
          </Button>
        </div>

        {/* Example queries */}
        {!searchResult && !searching && (
          <div className="mt-3 flex flex-wrap gap-2">
            {EXAMPLE_QUERIES.map(q => (
              <button
                key={q}
                type="button"
                onClick={() => { setSearchQuery(q); }}
                className="text-xs px-3 py-1.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-primary-100 dark:hover:bg-vivid-purple-dark/40 hover:text-primary-700 dark:hover:text-vivid-cyan-bright transition-colors"
              >
                {q}
              </button>
            ))}
          </div>
        )}

        {searchError && (
          <Alert type="error" className="mt-4">{searchError}</Alert>
        )}

        {searching && (
          <div className="mt-6 flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
            <Spinner size="sm" />
            Querying Nabhos AI…
          </div>
        )}

        {searchResult && (
          <div className="mt-6 space-y-3">
            <div className="p-4 bg-primary-50 dark:bg-vivid-purple-dark/20 rounded-lg border border-primary-200 dark:border-vivid-purple/30">
              <h3 className="font-semibold text-primary-900 dark:text-vivid-cyan-bright mb-2 flex items-center gap-2">
                <Brain className="w-4 h-4" />
                Nabhos Answer
              </h3>
              <p className="text-primary-800 dark:text-gray-200 whitespace-pre-wrap text-sm leading-relaxed">
                {searchResult.answer}
              </p>
            </div>

            <div className="flex items-center justify-between flex-wrap gap-2">
              {searchResult.sources.length > 0 && (
                <p className="text-xs text-gray-400 dark:text-gray-500">
                  Sources: {searchResult.sources.length} mission analysis record{searchResult.sources.length > 1 ? 's' : ''}
                </p>
              )}
              {searchResult.fallback && (
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  ⚠ Data-aware mode — Gemini API key not configured
                </p>
              )}
            </div>

            <button
              type="button"
              onClick={() => { setSearchResult(null); setSearchQuery(''); }}
              className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 underline"
            >
              Clear
            </button>
          </div>
        )}
      </Card>

      {/* ── Quick Stats ── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">AI Analyses</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {loadingData ? '–' : analyses.length}
              </p>
            </div>
            <Brain className="w-8 h-8 text-primary-600 dark:text-vivid-lime-bright" />
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Missions Analysed</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {loadingData ? '–' : missions.length}
              </p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-600 dark:text-vivid-lime-bright" />
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Avg. AI Confidence</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {loadingData ? '–' : analyses.length > 0 ? `${Math.round(totalConfidence * 100)}%` : '—'}
              </p>
            </div>
            <TrendingUp className="w-8 h-8 text-blue-600 dark:text-vivid-cyan-bright" />
          </div>
        </Card>
      </div>

      {/* ── Recent Analyses ── */}
      <Card>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
          Recent AI Analyses
        </h2>

        {loadingData ? (
          <div className="flex justify-center py-8"><Spinner size="md" /></div>
        ) : analyses.length === 0 ? (
          <div className="text-center py-12">
            <Activity className="w-12 h-12 text-gray-400 dark:text-gray-600 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-300 mb-2">No analyses yet</p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Upload drone imagery to a mission to trigger the AI diagnosis pipeline.
            </p>
            <Button variant="outline" size="sm"
              onClick={() => navigate(hasOilGas ? '/oil-gas/flight-analysis' : '/agrotech/flight-analysis')}>
              Go to Flight Data &amp; Analysis
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {analyses.slice(0, 10).map((a) => (
              <div
                key={a.id}
                className="flex items-center gap-4 p-4 border border-gray-200 dark:border-vivid-purple-dark/50 rounded-lg hover:border-primary-300 dark:hover:border-vivid-cyan-bright/50 hover:bg-primary-50 dark:hover:bg-vivid-purple-dark/20 transition-colors cursor-pointer"
                onClick={() => navigate(`/missions/${a.mission_id}`)}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-semibold px-2 py-0.5 rounded bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300 capitalize">
                      {a.analysis_type}
                    </span>
                    {a.metadata?.severity_level && (
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded ${
                        a.metadata.severity_level === 'high' || a.metadata.severity_level === 'critical'
                          ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                          : 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300'
                      }`}>
                        {a.metadata.severity_level.toUpperCase()}
                      </span>
                    )}
                    {a.confidence != null && (
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {Math.round(a.confidence * 100)}% conf.
                      </span>
                    )}
                  </div>
                  {a.metadata?.issue_classification && (
                    <p className="text-sm font-medium text-gray-900 dark:text-white mt-1">
                      {a.metadata.issue_classification}
                    </p>
                  )}
                  <div className="flex items-center gap-3 mt-1 text-xs text-gray-500 dark:text-gray-400 flex-wrap">
                    {a.metadata?.affected_area_ha != null && (
                      <span>{a.metadata.affected_area_ha} ha affected</span>
                    )}
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatDate(a.created_at)}
                    </span>
                  </div>
                </div>

                <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* ── Missions with AI Analysis ── */}
      {missions.length > 0 && (
        <Card>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            Missions with Completed AI Analysis
          </h2>
          <div className="space-y-3">
            {missions.slice(0, 5).map((m) => (
              <div
                key={m.id}
                className="flex items-center gap-4 p-4 border border-gray-200 dark:border-vivid-purple-dark/50 rounded-lg hover:border-primary-300 dark:hover:border-vivid-cyan-bright/50 hover:bg-primary-50 dark:hover:bg-vivid-purple-dark/20 transition-colors cursor-pointer"
                onClick={() => navigate(`/missions/${m.id}`)}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{m.name}</p>
                  {m.metadata?.diagnosis && (
                    <p className="text-xs text-violet-700 dark:text-violet-300 mt-0.5 truncate">
                      {m.metadata.diagnosis}
                      {m.metadata.confidence != null && ` — ${Math.round(m.metadata.confidence * 100)}% confidence`}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {m.metadata?.severity && (
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded ${
                      m.metadata.severity === 'high'
                        ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                        : 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300'
                    }`}>
                      {m.metadata.severity.toUpperCase()}
                    </span>
                  )}
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* ── Analysis types info — vertical-aware ── */}
      {hasOilGas ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card hover className="text-center">
            <Flame className="w-8 h-8 text-orange-600 dark:text-orange-400 mx-auto mb-3" />
            <h3 className="font-semibold text-gray-900 dark:text-white">OGI Leak Detection</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
              Methane plume quantification and gas-cloud bounding from FLIR GF-series OGI radiometric video
            </p>
          </Card>

          <Card hover className="text-center">
            <Thermometer className="w-8 h-8 text-red-600 dark:text-red-400 mx-auto mb-3" />
            <h3 className="font-semibold text-gray-900 dark:text-white">Thermal Hotspot Analysis</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
              Land surface temperature anomaly detection, flare intensity mapping, and compressor heat baselines
            </p>
          </Card>

          <Card hover className="text-center">
            <ShieldCheck className="w-8 h-8 text-blue-600 dark:text-vivid-cyan-bright mx-auto mb-3" />
            <h3 className="font-semibold text-gray-900 dark:text-white">Incident & Audit Chain</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
              SHA-256 sealed evidence chain from satellite detection through to ERP work order for regulatory submission
            </p>
          </Card>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card hover className="text-center">
            <TrendingUp className="w-8 h-8 text-green-600 dark:text-vivid-lime-bright mx-auto mb-3" />
            <h3 className="font-semibold text-gray-900 dark:text-white">Vegetation Health</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
              NDVI / NDRE decline, crop stress, and disease detection from multispectral imagery
            </p>
          </Card>

          <Card hover className="text-center">
            <AlertTriangle className="w-8 h-8 text-yellow-600 dark:text-vivid-yellow-bright mx-auto mb-3" />
            <h3 className="font-semibold text-gray-900 dark:text-white">Pest &amp; Disease</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
              Fungal, bacterial, and insect infestation diagnosis with chemical treatment recommendations
            </p>
          </Card>

          <Card hover className="text-center">
            <CheckCircle className="w-8 h-8 text-blue-600 dark:text-vivid-cyan-bright mx-auto mb-3" />
            <h3 className="font-semibold text-gray-900 dark:text-white">Remediation Plan</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
              Precision application rates, buffer zones, and follow-up survey scheduling
            </p>
          </Card>
        </div>
      )}

    </div>
  );
}

// Made with Bob
